/*
 * Office.js document-suggestion operations, ported from suggestions-panel.js.
 *
 * The originals mutated the global `state`; here they take a DocSuggestionSession
 * and mutate its `appliedChangesInParagraph` / `docSuggestions` in place (the
 * caller writes those back to the store). The rendering + getFriendlyRule
 * cross-import is gone — this module is Office-only.
 *
 * All apply paths share applyPendingChangesBatched: every word search is queued
 * before a single sync, every replacement before another, and the sentence-scoped
 * fallback adds syncs only when a primary search misses. Applying N suggestions
 * costs 3 round trips (5 with fallbacks) instead of 3-5 each.
 */

import type { DocReviewFinding, DocSuggestion, DocSuggestionSession } from "../state/types";
import { isDocSuggestion, occurrenceIndexBefore } from "./commentFindings";

/** Select/highlight the current suggestion's range in the Word document. */
export async function syncHighlightDocSuggestion(session: DocSuggestionSession): Promise<void> {
  if (
    session.currentDocSuggestionIndex >= session.docSuggestions.length ||
    session.currentDocSuggestionIndex < 0
  )
    return;
  const change = session.docSuggestions[session.currentDocSuggestionIndex];
  if (!change || !isDocSuggestion(change)) return;

  try {
    await Word.run(async (context) => {
      const paragraphs = context.document.body.paragraphs;
      paragraphs.load("items");
      await context.sync();

      const paraIndex = (change.paragraphIndex ?? 0) - 1;
      if (paraIndex >= paragraphs.items.length || paraIndex < 0) return;
      const para = paragraphs.items[paraIndex];

      const originalText = session.originalParagraphTexts[change.paragraphIndex ?? 0];
      if (!originalText) return;

      const before = String(change.before ?? "");
      const beforePosition = originalText.substring(0, change.position ?? 0);
      let targetOccurrenceIndex = 0;
      let pos = beforePosition.indexOf(before);
      while (pos !== -1) {
        targetOccurrenceIndex++;
        pos = beforePosition.indexOf(before, pos + 1);
      }

      const replacedBeforeThis = (
        session.appliedChangesInParagraph[change.paragraphIndex ?? 0] || []
      ).filter(
        (prevChange) =>
          (prevChange.position ?? 0) < (change.position ?? 0) && prevChange.before === change.before
      ).length;

      const currentOccurrenceIndex = targetOccurrenceIndex - replacedBeforeThis;

      // Queue the word search and the sentence fallback together so one sync
      // resolves both instead of paying a round trip per attempt.
      const searchResult = para.search(before, { matchCase: true });
      searchResult.load("items");
      const sentenceBefore = String(change.sentenceBefore ?? "");
      const sentenceSearch = sentenceBefore
        ? para.search(sentenceBefore, { matchCase: true })
        : null;
      if (sentenceSearch) sentenceSearch.load("items");
      await context.sync();

      if (searchResult.items.length > currentOccurrenceIndex && currentOccurrenceIndex >= 0) {
        searchResult.items[currentOccurrenceIndex].select();
        await context.sync();
      } else if (sentenceSearch && sentenceSearch.items.length > 0) {
        sentenceSearch.items[0].select();
        await context.sync();
      }
    });
  } catch (err) {
    console.error("Highlight sync error:", err);
  }
}

type SearchResult = ReturnType<Word.Paragraph["search"]>;

/** A suggestion resolved against the loaded paragraph list, ready to apply. */
interface PendingChange {
  change: DocSuggestion;
  /** 0-based index into paragraphs.items. */
  paraIndex: number;
  /** Which occurrence of `change.before` in the paragraph to replace. */
  occurrenceIndex: number;
}

/**
 * Apply a batch of pending changes with the minimum number of syncs.
 *
 * Pass 1 queues every primary word search (deduped per paragraph + term) and
 * resolves them in one sync. Pass 2 queues every replacement; changes whose
 * search missed fall back to the sentence-scoped path. The sentence searches
 * ride the same sync as the replacements (queued operations execute in order,
 * so they see post-replacement text exactly as the previous per-step syncs did).
 *
 * `pending` must be sorted bottom-up (last paragraph/position first) so applying
 * replacements last-to-first keeps the not-yet-applied ranges valid. Only
 * primary-path applications are recorded in appliedChangesInParagraph, matching
 * the original per-suggestion behavior.
 */
async function applyPendingChangesBatched(
  context: Word.RequestContext,
  paragraphs: Word.ParagraphCollection,
  session: DocSuggestionSession,
  pending: PendingChange[]
): Promise<void> {
  const searchCache = new Map<string, SearchResult>();
  for (const { change, paraIndex } of pending) {
    const key = `${paraIndex} ${change.before}`;
    if (!searchCache.has(key)) {
      const sr = paragraphs.items[paraIndex].search(String(change.before ?? ""), {
        matchCase: true,
      });
      sr.load("items");
      searchCache.set(key, sr);
    }
  }
  await context.sync(); // all primary searches resolved at once

  const fallbackChanges: PendingChange[] = [];
  for (const item of pending) {
    const { change, paraIndex, occurrenceIndex } = item;
    const paragraphKey = change.paragraphIndex ?? 0;
    const sr = searchCache.get(`${paraIndex} ${change.before}`);

    if (sr && occurrenceIndex >= 0 && sr.items.length > occurrenceIndex) {
      sr.items[occurrenceIndex].insertText(String(change.after ?? ""), Word.InsertLocation.replace);
      if (!session.appliedChangesInParagraph[paragraphKey]) {
        session.appliedChangesInParagraph[paragraphKey] = [];
      }
      session.appliedChangesInParagraph[paragraphKey].push(change);
    } else {
      fallbackChanges.push(item);
    }
  }

  const sentenceCache = new Map<string, SearchResult>();
  for (const { change, paraIndex } of fallbackChanges) {
    const sentenceBefore = String(change.sentenceBefore ?? "");
    if (!sentenceBefore) continue;
    const key = `${paraIndex} ${sentenceBefore}`;
    if (!sentenceCache.has(key)) {
      const ss = paragraphs.items[paraIndex].search(sentenceBefore, { matchCase: true });
      ss.load("items");
      sentenceCache.set(key, ss);
    }
  }
  await context.sync(); // primary replacements committed + sentence searches resolved

  if (fallbackChanges.length === 0) return;

  const wordSearches: Array<{ change: DocSuggestion; ws: SearchResult }> = [];
  for (const { change, paraIndex } of fallbackChanges) {
    const ss = sentenceCache.get(`${paraIndex} ${change.sentenceBefore}`);
    if (ss && ss.items.length > 0) {
      const ws = ss.items[0].search(String(change.before ?? ""), { matchCase: true });
      ws.load("items");
      wordSearches.push({ change, ws });
    }
  }
  await context.sync(); // all sentence-scoped word searches resolved at once

  for (const { change, ws } of wordSearches) {
    if (ws.items.length > 0) {
      ws.items[0].insertText(String(change.after ?? ""), Word.InsertLocation.replace);
    }
  }
  await context.sync(); // fallback replacements committed at once
}

/** Sort bottom-up so applying replacements last-to-first keeps pending ranges valid. */
function sortBottomUp(changes: DocSuggestion[]): DocSuggestion[] {
  return changes.slice().sort((a, b) => {
    const aPara = a.paragraphIndex ?? 0;
    const bPara = b.paragraphIndex ?? 0;
    if (bPara !== aPara) return bPara - aPara;
    return (b.position ?? 0) - (a.position ?? 0);
  });
}

/**
 * Apply several suggestions in one Word.run with batched syncs; records each
 * primary-path application in appliedChangesInParagraph.
 */
export async function applyDocSuggestions(
  session: DocSuggestionSession,
  changes: DocSuggestion[]
): Promise<void> {
  if (changes.length === 0) return;
  const sorted = sortBottomUp(changes);

  await Word.run(async (context) => {
    const paragraphs = context.document.body.paragraphs;
    paragraphs.load("items");
    await context.sync();

    const pending: PendingChange[] = [];
    for (const change of sorted) {
      const paraIndex = (change.paragraphIndex ?? 0) - 1;
      if (paraIndex < 0 || paraIndex >= paragraphs.items.length) continue;
      const paragraphKey = change.paragraphIndex ?? 0;
      const originalText = session.originalParagraphTexts[paragraphKey];
      if (!originalText) continue;

      const targetOccurrenceIndex = occurrenceIndexBefore(
        originalText,
        String(change.before ?? ""),
        change.position ?? 0
      );

      // Earlier same-word applications shifted the live occurrence numbering
      // relative to the original text. Within this batch no adjustment is
      // needed: every search resolves against the same pre-batch snapshot and
      // the bottom-up order keeps those ranges valid.
      const replacedBeforeThis = (session.appliedChangesInParagraph[paragraphKey] || []).filter(
        (prevChange) =>
          (prevChange.position ?? 0) < (change.position ?? 0) && prevChange.before === change.before
      ).length;

      pending.push({
        change,
        paraIndex,
        occurrenceIndex: targetOccurrenceIndex - replacedBeforeThis,
      });
    }

    await applyPendingChangesBatched(context, paragraphs, session, pending);
  });
}

/** Apply the active suggestion; records it in appliedChangesInParagraph. */
export async function applyDocSuggestion(session: DocSuggestionSession): Promise<void> {
  if (
    session.currentDocSuggestionIndex >= session.docSuggestions.length ||
    session.currentDocSuggestionIndex < 0
  )
    return;
  const change = session.docSuggestions[session.currentDocSuggestionIndex];
  if (!change || !isDocSuggestion(change)) return;

  await applyDocSuggestions(session, [change]);
}

/**
 * Apply every remaining suggestion matching `ruleId`, batched to ~3 syncs.
 * Mutates session.appliedChangesInParagraph and removes applied suggestions
 * from session.docSuggestions.
 */
export async function applyAllRuleSuggestions(
  session: DocSuggestionSession,
  ruleId: string
): Promise<void> {
  const remainingSuggestions = session.docSuggestions.slice(session.currentDocSuggestionIndex);
  const matchingSuggestions = remainingSuggestions.filter(
    (s): s is DocSuggestion & { findingType: "change" } => isDocSuggestion(s) && s.ruleId === ruleId
  );

  if (matchingSuggestions.length === 0) return;

  const sorted = sortBottomUp(matchingSuggestions);

  await Word.run(async (context) => {
    const paragraphs = context.document.body.paragraphs;
    paragraphs.load("items");
    await context.sync();

    // Unlike the single-accept path, occurrence indices here are taken straight
    // from the original text (no replacedBeforeThis adjustment) — preserved
    // as-is from the port.
    const pending: PendingChange[] = [];
    for (const change of sorted) {
      const paraIndex = (change.paragraphIndex ?? 0) - 1;
      if (paraIndex < 0 || paraIndex >= paragraphs.items.length) continue;
      const paragraphKey = change.paragraphIndex ?? 0;
      const originalText = session.originalParagraphTexts[paragraphKey];
      if (!originalText) continue;

      const occurrenceIndex = occurrenceIndexBefore(
        originalText,
        String(change.before ?? ""),
        change.position ?? 0
      );
      pending.push({ change, paraIndex, occurrenceIndex });
    }

    await applyPendingChangesBatched(context, paragraphs, session, pending);
  });

  // Remove applied suggestions from the active array.
  const appliedSuggestions = new Set<DocReviewFinding>(matchingSuggestions);
  session.docSuggestions = session.docSuggestions.filter((s) => !appliedSuggestions.has(s));
}
